import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { CollapseButton } from '@ui/buttons';
import { getDocumentStatus, groupDocumentsByLabel } from '@utils/documentVault';
import { VaultTableRow } from '../table/VaultTableRow';

const FolderWrapper = styled.div`
  padding-bottom: 8px;
`;

const FolderHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  padding: 10px 8px;
  cursor: pointer;

  border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};

  &:hover {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;

// The shared collapse chevron sits on a baseline meant for section headings, not a dense row.
const CollapseWrapper = styled.div`
  display: flex;
  align-items: center;

  div {
    padding-bottom: 0;
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const FolderContents = styled.div`
  padding-left: 20px;
`;

const Folder = ({ label, docs, statusLookup, density, onShowDetails, startOpen }) => {
  const [isOpen, setIsOpen] = React.useState(startOpen);

  return (
    <FolderWrapper>
      <FolderHeader onClick={() => setIsOpen(!isOpen)}>
        <CollapseWrapper>
          <CollapseButton isCollapsed={!isOpen} setCollapsed={(collapsed) => setIsOpen(!collapsed)} />
        </CollapseWrapper>
        <Body5 bold>{label}</Body5>
        <Body5 isLowContrast>({docs.length})</Body5>
      </FolderHeader>
      {isOpen ? (
        <FolderContents>
          {docs.map((doc) => (
            <VaultTableRow
              key={doc.id}
              doc={doc}
              status={getDocumentStatus(doc, statusLookup)}
              density={density}
              onShowDetails={onShowDetails}
            />
          ))}
        </FolderContents>
      ) : null}
    </FolderWrapper>
  );
};

/*
  Groups the same way the folder tree on Document Management does, but renders vault rows: the
  shared DocumentTree carries delete and edit actions, which this mode deliberately does not have.

  Shows the filtered set, not the whole library -- the view toggle changes the shape of the
  results, never which results.
*/
export const VaultTreeView = ({ docs, statusLookup, density, onShowDetails }) => {
  const groups = React.useMemo(() => groupDocumentsByLabel(docs), [docs]);

  return (
    <Container>
      {groups.map((group, index) => (
        <Folder
          key={group.label}
          label={group.label}
          docs={group.docs}
          statusLookup={statusLookup}
          density={density}
          onShowDetails={onShowDetails}
          startOpen={groups.length <= 5 || index === 0}
        />
      ))}
    </Container>
  );
};
